import { and, eq } from "drizzle-orm";
import { ArrowDropDown, ArrowDropUp, Check } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Chip, IconButton, Menu, MenuItem, Stack, TextField, Tooltip, Typography, useTheme } from "@mui/material";
import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { createLogger } from "@/logger";

const log = createLogger("feed");

import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { client } from "@/config";
import { db } from "@/db/db";
import { workspaceMembership } from "@/db/schema";
import Locator from "@/locator";
import { removeMember, updateMemberRole } from "@/models/actions/clientActions";
import { DataContext } from "@/models/DataProvider";
import { useReactiveSelectQuery } from "@/models/hooks/useReactiveSelectQuery.ts";
import { bulkUpsertAccount } from "@/models/repositories/Account";
import { bulkUpsertDirectWsInvitation } from "@/models/repositories/DirectWsInvitation";
import { bulkUpsertWorkspaceMembership, workspaceMembersTableSelect } from "@/models/repositories/WorkspaceMembership";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider";
import { TrackingContext } from "@/models/TrackingStateProvider";
import { UserManagementContext } from "@/models/UserManagementContextProvider";
import DataTable from "../DataTable";
import Loading from "../Loading/Index";

export default function WorkspaceMembersTable({ newDesign }: { newDesign?: boolean }) {
  const { availableWorkspaceRoles, getWorkspaceRoleLabel } = useContext(DataContext);
  const { searchFilter, setSearchFilter, activeSearchField } = useContext(UserManagementContext);
  const { myAccountId } = useContext(MyAccountContext);
  const { ampli } = useContext(TrackingContext);
  const { currentWorkspaceId: workspaceId } = useContext(WorkspaceContext);
  const theme = useTheme();

  const [roleMenuEl, setRoleMenuEl] = useState<null | HTMLElement>(null);
  const [roleMenuOpen, setRoleMenuOpen] = useState<string>();
  const [revokingId, setRevokingId] = useState<string>();
  const [updatingMemberId, setUpdatingMemberId] = useState<string>();
  const roleOptions = Array?.from(availableWorkspaceRoles?.values())?.filter((r) => r?.enabled);

  useEffect(() => {
    const f = async () => {
      try {
        log.info("[WORKSPACE PROVIDER] Loading workspace details", {
          workspaceId,
          myAccountId,
        });
        if (!workspaceId || !myAccountId) return;
        const membership = await db.query.workspaceMembership.findFirst({
          where: and(
            eq(workspaceMembership.id, workspaceId),
            eq(workspaceMembership.accountId, myAccountId),
            eq(workspaceMembership.status, "active"),
          ),
        });
        if (membership?.role === "limitedMember") return;
        await client.getWorkspaceInvitations(workspaceId).then((resp) => {
          log.info("[WORKSPACE PROVIDER] Creating workspace memberships", {
            resp,
          });
          if (resp?.workspaceMemberships?.length) bulkUpsertWorkspaceMembership(resp?.workspaceMemberships || []);
          if (resp?.accounts?.length) bulkUpsertAccount(resp?.accounts || []);
          if (resp?.directInvitations?.length) bulkUpsertDirectWsInvitation(resp?.directInvitations || []);
        });
      } catch (e) {
        console.error("Error loading workspace details", e);
      }
    };
    f();
  }, [myAccountId, workspaceId]);

  const closeRoleMenu = useCallback(() => {
    setRoleMenuEl(null);
    setRoleMenuOpen("");
  }, []);

  // Clear menu state if anchor element becomes disconnected from the DOM
  useEffect(() => {
    if (roleMenuEl && !roleMenuEl.isConnected) {
      closeRoleMenu();
    }
  }, [roleMenuEl, closeRoleMenu]);

  const workspaceMembers =
    useReactiveSelectQuery(
      workspaceMembersTableSelect,
      [{ workspaceId }],
      "WorkspaceMembersTable:workspaceMembersTableSelect",
    ) ?? [];

  const removeWorkspaceMember = useCallback(
    async (workspaceMembershipId: string) => {
      try {
        setRevokingId(workspaceMembershipId);
        if (workspaceId) {
          ampli.removeWorkspaceAccess();
          await removeMember(workspaceId, [workspaceMembershipId]);
        }
      } catch (e) {
        console.log(e);
      }
      setRevokingId("");
    },
    [workspaceId],
  );

  const updateWorkspaceMemberRole = useCallback(
    async (workspaceId, membershipId, currentRole, newRole) => {
      try {
        setUpdatingMemberId(membershipId);
        if (currentRole === "admin" && (newRole === "member" || newRole === "limitedMember")) {
          ampli.downgradePermissionMember();
        }
        if ((currentRole === "member" || currentRole === "limitedMember") && newRole === "admin") {
          ampli.upgradePermissionAdmin();
        }
        await updateMemberRole(workspaceId, [membershipId], newRole);
      } catch (e) {
        console.log(e);
      }
      setUpdatingMemberId("");
    },
    [updateMemberRole],
  );

  const currentMember = useMemo(
    () => workspaceMembers.find((m) => m.membershipId === roleMenuOpen),
    [workspaceMembers, roleMenuOpen],
  );

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Full name",
        minSize: 200,
        meta: { flex: 1 },
        cell: ({ row }) => (
          <>
            {row.original.mine && (
              <Box sx={{ pr: 2 }}>
                <Chip label="Me" color="info" />
              </Box>
            )}
            <Typography fontWeight={700}>{row.original.name}</Typography>
          </>
        ),
      },
      {
        id: "contact",
        accessorFn: (row) => row.email || row.phoneNumber || row.id,
        header: "Contact",
        minSize: 200,
        meta: { flex: 1 },
        cell: ({ row }) => {
          const contact = row.original.email || row.original.phoneNumber || row.original.id;
          return (
            <Tooltip title={contact}>
              <Box>{contact}</Box>
            </Tooltip>
          );
        },
      },
      {
        accessorKey: "role",
        header: "Type",
        minSize: 120,
        maxSize: 200,
        meta: { flex: 1 },
        enableGlobalFilter: false,
        cell: ({ row }) => {
          const memberTestId = row.original.email || row.original.phoneNumber;
          return (
            <Box>
              {row.original.mine ? (
                <Typography sx={{ textTransform: "capitalize", pr: 4.2, pl: 1 }}>
                  {getWorkspaceRoleLabel(row.original.role) || row.original.role}
                </Typography>
              ) : (
                <>
                  <IconButton
                    onClick={(e) => {
                      setRoleMenuEl(e.currentTarget);
                      setRoleMenuOpen(row.original.membershipId);
                    }}
                    aria-label={Locator.workspaceNav.members.activeMembers.roleSelect}
                    data-testid={Locator.workspaceNav.members.activeMembers.roleSelectTestId(memberTestId)}
                    sx={{
                      textTransform: "capitalize",
                      background: "transparent",
                    }}
                  >
                    <Typography>{getWorkspaceRoleLabel(row.original.role) || row.original.role}</Typography>
                    {row.original.membershipId === roleMenuOpen ? (
                      <ArrowDropUp role="img" />
                    ) : (
                      <ArrowDropDown role="img" />
                    )}
                  </IconButton>
                </>
              )}
            </Box>
          );
        },
      },
      {
        id: "remove",
        header: "",
        size: 115,
        enableSorting: false,
        enableGlobalFilter: false,
        meta: { align: "right" as const },
        cell: ({ row }) => {
          const memberTestId = row.original.email || row.original.phoneNumber;
          if (!row.original.mine) {
            return (
              <LoadingButton
                loading={row.original.membershipId === revokingId}
                onClick={() => removeWorkspaceMember(row.original.membershipId)}
                aria-label={Locator.workspaceNav.members.activeMembers.revokeButton}
                data-testid={Locator.workspaceNav.members.activeMembers.revokeTestId(memberTestId)}
                sx={{
                  padding: "8px 12px",
                  fontSize: "0.875rem",
                  background: theme.palette.secondary.dark,
                  maxWidth: "85px",
                  lineHeight: 1,
                }}
              >
                REMOVE
              </LoadingButton>
            );
          }
          return null;
        },
      },
    ],
    [getWorkspaceRoleLabel, roleMenuOpen, revokingId, theme, removeWorkspaceMember],
  );

  const table = useReactTable({
    data: workspaceMembers,
    columns,
    state: { globalFilter: searchFilter },
    onGlobalFilterChange: setSearchFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 20 } },
  });

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: newDesign ? "auto" : 1300,
        overflow: "hidden",
        height: "100%",
      }}
    >
      {workspaceMembers?.length > 0 ? (
        <DataTable
          table={table}
          contentFit={newDesign}
          pageSizeOptions={[20, 60, 100]}
          toolbar={
            <TextField
              inputRef={activeSearchField}
              placeholder="Search for members"
              value={searchFilter ?? ""}
              onChange={(e) => setSearchFilter(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              sx={{ mb: 1 }}
            />
          }
        />
      ) : (
        <Stack
          sx={{
            width: "100%",
            py: 1.5,
            px: 2,
            background: theme.palette.secondary.main,
            borderRadius: 1.5,
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>No members</Typography>
        </Stack>
      )}
      {roleMenuEl?.isConnected && (
        <Menu
          anchorEl={roleMenuEl}
          open={Boolean(roleMenuOpen)}
          onClose={closeRoleMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          sx={{ mt: 1, ml: 1 }}
          slotProps={{ paper: { sx: { width: 185, px: 1, py: 1.5 } } }}
        >
          {roleOptions?.map((r) => (
            <MenuItem
              sx={{
                maxHeight: 40,
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: "4px",
                background: currentMember?.role === r?.role ? theme.palette.secondary.main : "transparent",
                border: currentMember?.role === r?.role ? "1.5px solid" : "none",
                borderColor: currentMember?.role === r?.role ? theme.palette.secondary.light : "none",
                fontWeight: 500,
                textTransform: "capitalize",
              }}
              key={`role-option-${r?.role}`}
              onClick={() => {
                updateWorkspaceMemberRole(workspaceId, roleMenuOpen, currentMember?.role, r?.role);
                closeRoleMenu();
              }}
              disabled={roleMenuOpen === updatingMemberId}
            >
              <Stack sx={{ position: "relative", maxWidth: 24, alignItems: "center" }}>
                {currentMember?.role === r?.role ? <Check /> : <Box sx={{ width: 24 }} />}
                {currentMember?.role !== r?.role && roleMenuOpen === updatingMemberId ? (
                  <Loading variant="disabled" size="tiny" />
                ) : null}
              </Stack>
              {r?.label || r?.role}
            </MenuItem>
          ))}
        </Menu>
      )}
    </Box>
  );
}
